import React, { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Loader2, Upload, X } from "lucide-react";
import { useAuth } from "@clerk/clerk-react";
import { fieldLabelClasses } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import {
  ACCEPTED_IMAGE_ATTR,
  ACCEPTED_IMAGE_TYPES,
  MAX_IMAGE_DIMENSION,
  MAX_UPLOAD_BYTES,
} from "../data/brandMock";
import { scanForQrCode } from "../qrCode";
import { uploadFile } from "../services/fileService";
import { FilePurpose, UploadedFile } from "../types/brand.types";
import LogoCropModal from "./LogoCropModal";

interface UploadFieldProps {
  label: string;
  /** Prompt shown while nothing is picked. */
  placeholder: string;
  hint?: string;
  /** What the file is for — the backend files it under the workspace by this. */
  purpose: FilePurpose;
  /**
   * Turn away any image without a readable QR code in it. For the payment QR,
   * where every other picture is the wrong picture.
   */
  requireQrCode?: boolean;
  /**
   * Routes a picked image through the crop dialog before it is uploaded.
   * Left unset for a picture that has to stay exactly what was picked — the
   * payment QR, which cropping would as likely break as fix.
   */
  crop?: boolean;
  value: UploadedFile | null;
  onValueChange: (file: UploadedFile | null) => void;
}

/** The picked file as a data URL, which is what the previews paint. */
const readDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(file);
  });

/**
 * Hands the thread back for long enough that React has painted whatever was
 * just set on it. A bare `setTimeout(0)` is not enough: React renders on its
 * own scheduled task, and that task can be queued behind this one.
 */
const nextFrame = () =>
  new Promise<void>((resolve) => {
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 0);
    }
  });

/**
 * The picture itself, decoded from the data URL already in hand — its
 * dimensions are read off it, and the QR scan reads its pixels, so the one
 * decode serves both.
 *
 * Rejecting is a result rather than a mishap: a file the browser cannot decode
 * is not the image its type claims it is, whatever its extension says.
 */
const decodeImage = (dataUrl: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("decode failed"));
    image.src = dataUrl;
  });

/**
 * The brand form's image picker — logo and payment QR. It stays in this
 * feature rather than the shared control kit because the rules it enforces
 * (the accepted types, the size ceiling) are the brand upload's, not every
 * form's.
 *
 * Picking a file uploads it there and then, rather than holding the bytes back
 * until Save: the form stores what the backend answers with, so saving sends a
 * `public_id` and nothing large. The row shows the picked file while that call
 * is in flight, so the wait reads as progress on something already chosen.
 *
 * Must be used inside a Clerk provider and an active session — the upload
 * carries a bearer token like every other call.
 */
export const UploadField: React.FC<UploadFieldProps> = ({
  label,
  placeholder,
  hint,
  purpose,
  requireQrCode = false,
  crop = false,
  value,
  onValueChange,
}) => {
  const { getToken } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState("");

  // The picture waiting on a crop decision. Held separately from `pending` —
  // nothing has been picked as far as the row is concerned until the dialog
  // is confirmed, so the existing value stays shown behind it.
  const [cropTarget, setCropTarget] = useState<{
    file: File;
    dataUrl: string;
  } | null>(null);

  // The preview that would not load, held as its own src rather than a flag —
  // so picking another file is a fresh attempt with nothing to reset.
  const [failedThumbnail, setFailedThumbnail] = useState<string | null>(null);

  // What is being worked on right now, shown in place of `value` until it is
  // done. The form is only handed the file once the backend has taken it, so a
  // failed upload leaves no half-file behind in the details. `stage` is what
  // the row says it is doing: reading a large picture for a QR code is a
  // second of held breath, and an unlabelled second reads as a hang.
  const [pending, setPending] = useState<{
    name: string;
    dataUrl: string;
    stage: "checking" | "uploading";
  } | null>(null);

  // The upload in flight, so picking again — or leaving the page — drops the
  // one before it rather than letting a slow first call land last.
  const uploadRef = useRef<AbortController | null>(null);
  useEffect(() => () => uploadRef.current?.abort(), []);

  /**
   * Carries a file — the original pick, or the crop dialog's output — through
   * the QR check (where asked for) and the upload itself.
   */
  const finishUpload = async (
    file: File,
    dataUrl: string,
    image?: HTMLImageElement
  ) => {
    uploadRef.current?.abort();
    const controller = new AbortController();
    uploadRef.current = controller;
    setPending({ name: file.name, dataUrl, stage: "checking" });

    try {
      // Last, and only where it is asked for: the scan is the most expensive
      // rule of the four and the one most worth not reaching.
      if (requireQrCode && image) {
        // The scan holds the thread, so the row above is given a frame to
        // paint the file and say what it is doing before it does it.
        await nextFrame();
        if (uploadRef.current !== controller) return;

        // `unavailable` — no canvas to read the pixels with — is not a refusal.
        // The rule is that the picture must be a QR code, not that the browser
        // must be able to prove it, and a user with nothing to show for their
        // upload but an error they cannot act on is the worse failure.
        if (scanForQrCode(image) === "not-found") {
          setError("That image is not a QR code. Upload your payment QR.");
          return;
        }
      }

      setPending({ name: file.name, dataUrl, stage: "uploading" });

      const stored = await uploadFile(() => getToken(), file, purpose, {
        signal: controller.signal,
      });

      // Superseded by a later pick, or unmounted: whatever came back belongs to
      // a field that has moved on.
      if (uploadRef.current !== controller) return;

      onValueChange({
        name: file.name,
        dataUrl,
        publicId: stored.publicId,
        url: stored.url,
      });
    } catch (uploadError) {
      if (uploadRef.current !== controller) return;
      setError(
        uploadError instanceof ApiError
          ? uploadError.message
          : "That file could not be uploaded. Check your connection and try again."
      );
      console.error(`${purpose} upload failed:`, uploadError);
    } finally {
      if (uploadRef.current === controller) {
        uploadRef.current = null;
        setPending(null);
      }
    }
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setError("Only PNG, JPEG & WebP files are allowed");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setError("File is larger than 1MB");
      return;
    }

    setError("");

    let dataUrl: string;
    try {
      dataUrl = await readDataUrl(file);
    } catch {
      setError("That file could not be read");
      return;
    }

    // The dimensions come after the two cheap rules, because they are the only
    // ones that cannot be answered from the file's own metadata — it has to be
    // decoded first, and there is no point decoding one already turned away.
    let image: HTMLImageElement;
    try {
      image = await decodeImage(dataUrl);
    } catch {
      setError("That file is not a readable image");
      return;
    }

    if (
      image.naturalWidth > MAX_IMAGE_DIMENSION ||
      image.naturalHeight > MAX_IMAGE_DIMENSION
    ) {
      setError(
        `Image is larger than ${MAX_IMAGE_DIMENSION} × ${MAX_IMAGE_DIMENSION} pixels`
      );
      return;
    }

    if (crop) {
      setCropTarget({ file, dataUrl });
      return;
    }

    await finishUpload(file, dataUrl, image);
  };

  const clear = () => {
    // A file being uploaded is also a file being cleared: dropping the call
    // stops its result from arriving after the field was emptied.
    uploadRef.current?.abort();
    uploadRef.current = null;
    setPending(null);
    setCropTarget(null);
    setError("");
    onValueChange(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const busy = pending !== null;
  const shown = pending ?? value;
  const thumbnailFailed = shown !== null && failedThumbnail === shown.dataUrl;

  return (
    <div>
      <span className={`${fieldLabelClasses} mb-1.5`}>{label}</span>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_ATTR}
        aria-label={label}
        disabled={busy}
        // The same file picked twice in a row is still a change worth acting
        // on — after a failed upload it is the natural way to retry.
        onClick={(event) => {
          (event.target as HTMLInputElement).value = "";
        }}
        onChange={(event) => void handleFile(event.target.files?.[0])}
        className="sr-only"
      />

      {shown ? (
        <div className="flex items-center gap-3 rounded-[6px] border border-line bg-white px-3.5 py-2.5">
          {/* A file loaded from the server is fetched from the API's origin,
              and that can fail. The row still has to name the file that is
              stored, so the thumbnail gives way to a plain mark rather than
              taking the whole row down with a broken image. */}
          {thumbnailFailed ? (
            <span
              aria-hidden="true"
              className="flex w-8 h-8 flex-shrink-0 items-center justify-center rounded bg-[#F4F7F8] text-ink-muted"
            >
              <ImageIcon className="w-4 h-4" />
            </span>
          ) : (
            <img
              src={shown.dataUrl}
              alt=""
              className={`w-8 h-8 rounded object-contain flex-shrink-0 ${
                busy ? "opacity-50" : ""
              }`}
              onError={() => setFailedThumbnail(shown.dataUrl)}
            />
          )}
          <span className="min-w-0 flex-1 truncate text-[14px] text-ink">
            {shown.name}
          </span>

          {pending ? (
            <span className="flex flex-shrink-0 items-center gap-1.5 text-[13px] text-ink-subtle">
              <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
              {pending.stage === "checking" ? "Checking…" : "Uploading…"}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="flex-shrink-0 text-[13px] font-medium text-brand hover:underline"
            >
              Replace
            </button>
          )}

          <button
            type="button"
            onClick={clear}
            aria-label={
              busy
                ? `Cancel ${label.toLowerCase()} upload`
                : `Remove ${label.toLowerCase()}`
            }
            className="flex-shrink-0 rounded p-1 text-ink-subtle hover:bg-[#F4F7F8] hover:text-ink transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex w-full items-center gap-2 rounded-[6px] border border-line bg-white px-3.5 py-2.5 3xl:py-3 text-left text-[14px] 3xl:text-[15px] text-ink-subtle hover:border-brand transition-colors"
        >
          <Upload className="w-4 h-4 flex-shrink-0 text-ink-muted" />
          {placeholder}
        </button>
      )}

      {error ? (
        <p className="mt-1.5 text-right text-[11px] text-red-500">{error}</p>
      ) : (
        hint && (
          <p className="mt-1.5 text-right text-[11px] 3xl:text-[12px] text-ink-subtle">
            {hint}
          </p>
        )
      )}

      {cropTarget && (
        <LogoCropModal
          open
          imageSrc={cropTarget.dataUrl}
          file={cropTarget.file}
          onCancel={() => {
            setCropTarget(null);
            if (inputRef.current) inputRef.current.value = "";
          }}
          onConfirm={(croppedFile, croppedDataUrl) => {
            setCropTarget(null);
            void finishUpload(croppedFile, croppedDataUrl);
          }}
        />
      )}
    </div>
  );
};

export default UploadField;
